import React, { useState, useEffect, useRef } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ReferenceLine
} from 'recharts';

// Component to display resource and production evolution
const PlotContent = ({ currentGameState, plotData }) => {
  const [selectedPlanetId, setSelectedPlanetId] = useState('1'); // Default to first planet
  const [chartData, setChartData] = useState([]);
  const [resizeKey, setResizeKey] = useState(0);
  
  // State for zoom and pan
  const [zoomState, setZoomState] = useState({
    resourceChart: {
      left: 'dataMin',
      right: 'dataMax',
      refAreaLeft: '',
      refAreaRight: '',
      top: 'dataMin',
      bottom: 'dataMax',
      animation: true
    },
    productionChart: {
      left: 'dataMin',
      right: 'dataMax',
      refAreaLeft: '',
      refAreaRight: '',
      top: 'dataMin',
      bottom: 'dataMax',
      animation: true
    }
  });
  
  // References to chart components
  const resourceChartRef = useRef(null);
  const productionChartRef = useRef(null);
  
  // Process plot data when it changes or when selected planet changes
  useEffect(() => {
    if (!plotData || Object.keys(plotData).length === 0) {
      console.log("No plot data available");
      return;
    }
    
    console.log("Plot data available for planets:", Object.keys(plotData));
    
    // Get data for selected planet
    const planetData = plotData[selectedPlanetId];
    if (!planetData || planetData.length === 0) {
      console.log(`No data for planet ${selectedPlanetId}`);
      setChartData([]);
      return;
    }
    
    console.log(`Found ${planetData.length} data points for planet ${selectedPlanetId}`);
    
    // Process data for chart - simple approach without tooltip logic
    const processedPlotData = planetData.map(point => {
      const { time, label, snapshot } = point;
      
      // If snapshot is null, return a minimal data point
      if (!snapshot) {
        return { 
          time, 
          label
        };
      }
      
      return {
        time,
        label,
        metal: snapshot.resources.metal,
        crystal: snapshot.resources.crystal,
        deuterium: snapshot.resources.deuterium,
        metalProd: snapshot.production.metal,
        crystalProd: snapshot.production.crystal,
        deuteriumProd: snapshot.production.deuterium,
        energyProd: snapshot.production.energy
      };
    });
    
    // Sort by time
    processedPlotData.sort((a, b) => a.time - b.time);
    
    // Log the processed data
    console.log("Processed chart data:", processedPlotData);
    
    setChartData(processedPlotData);
  }, [plotData, selectedPlanetId]);

  // Format time for x-axis
  const formatXAxis = (time) => {
    const hours = Math.floor(time);
    const minutes = Math.floor((time - hours) * 60);
    return `${hours}h ${minutes}m`;
  };
  
  // Calculate domain with padding for the x-axis
  const calculateXDomain = (data, leftBound = 'dataMin', rightBound = 'dataMax') => {
    if (!data || data.length === 0) return ['dataMin', 'dataMax'];
    
    // Get min and max time values
    const minTime = Math.min(...data.map(d => d.time));
    const maxTime = Math.max(...data.map(d => d.time));
    
    // If we're using the default bounds, add padding to the full dataset
    if (leftBound === 'dataMin' && rightBound === 'dataMax') {
      const padding = (maxTime - minTime) * 0.05;
      return [minTime - padding, maxTime + padding];
    }
    
    // If we're zoomed in, add padding to the zoomed area
    const range = rightBound - leftBound;
    const padding = range * 0.05;
    return [leftBound - padding, rightBound + padding];
  };
  
  // Generate evenly spaced ticks for the x-axis
  const generateEvenlySpacedTicks = (data, leftBound = 'dataMin', rightBound = 'dataMax') => {
    if (!data || data.length === 0) return [];
    
    // Get min and max time values with padding
    const [minDomain, maxDomain] = calculateXDomain(data, leftBound, rightBound);
    
    // Determine number of ticks based on chart width
    const tickCount = 6; // Adjust this number for more or fewer ticks
    
    // Generate evenly spaced ticks
    const ticks = [];
    const step = (maxDomain - minDomain) / (tickCount - 1);
    
    for (let i = 0; i < tickCount; i++) {
      const tickValue = minDomain + i * step;
      ticks.push(Number(tickValue.toFixed(2))); // Round to 2 decimal places
    }
    
    return ticks;
  };
  
  // Calculate y-axis domain based on visible data
  const calculateYDomain = (data, left, right, keys) => {
    if (!data || data.length === 0) return [0, 'auto'];
    
    // Filter data to only include points within the visible x range
    const visibleData = data.filter(d => d.time >= left && d.time <= right);
    
    if (visibleData.length === 0) return [0, 'auto'];
    
    // Find the max value across all specified keys
    let maxValue = 0;
    visibleData.forEach(d => {
      keys.forEach(key => {
        if (d[key] && d[key] > maxValue) {
          maxValue = d[key];
        }
      });
    });
    
    // Add 15% padding to the top
    maxValue = maxValue * 1.15;
    
    return [0, maxValue];
  };
  
  // Zoom and pan handlers
  const handleMouseDown = (chartId, e) => {
    if (!e) return;
    
    setZoomState(prev => ({
      ...prev,
      [chartId]: {
        ...prev[chartId],
        refAreaLeft: e.activeLabel
      }
    }));
  };
  
  const handleMouseMove = (chartId, e) => {
    if (!e || !zoomState[chartId].refAreaLeft) return;
    
    setZoomState(prev => ({
      ...prev,
      [chartId]: {
        ...prev[chartId],
        refAreaRight: e.activeLabel
      }
    }));
  };
  
  const handleMouseUp = (chartId) => {
    const { refAreaLeft, refAreaRight } = zoomState[chartId];
    
    if (refAreaLeft === refAreaRight || !refAreaRight) {
      setZoomState(prev => ({
        ...prev,
        [chartId]: {
          ...prev[chartId],
          refAreaLeft: '',
          refAreaRight: ''
        }
      }));
      return;
    }
    
    // xAxis domain
    let left = Math.min(refAreaLeft, refAreaRight);
    let right = Math.max(refAreaLeft, refAreaRight);
    
    // Add padding to the zoomed area
    const range = right - left;
    const padding = range * 0.05;
    left = left - padding;
    right = right + padding;
    
    // Calculate yAxis domains based on visible data for both charts
    const resourceKeys = ['metal', 'crystal', 'deuterium'];
    const productionKeys = ['metalProd', 'crystalProd', 'deuteriumProd', 'energyProd'];
    
    const resourceYDomain = calculateYDomain(chartData, left, right, resourceKeys);
    const productionYDomain = calculateYDomain(chartData, left, right, productionKeys);
    
    // Update both charts with the same x-axis domain
    setZoomState(prev => ({
      ...prev,
      resourceChart: {
        ...prev.resourceChart,
        refAreaLeft: '',
        refAreaRight: '',
        left,
        right,
        top: resourceYDomain[0],
        bottom: resourceYDomain[1],
        animation: true
      },
      productionChart: {
        ...prev.productionChart,
        refAreaLeft: '',
        refAreaRight: '',
        left,
        right,
        top: productionYDomain[0],
        bottom: productionYDomain[1],
        animation: true
      }
    }));
  };
  
  const handleZoomOut = () => {
    // Reset both charts simultaneously
    setZoomState({
      resourceChart: {
        left: 'dataMin',
        right: 'dataMax',
        refAreaLeft: '',
        refAreaRight: '',
        top: 0,
        bottom: 'auto',
        animation: true
      },
      productionChart: {
        left: 'dataMin',
        right: 'dataMax',
        refAreaLeft: '',
        refAreaRight: '',
        top: 0,
        bottom: 'auto',
        animation: true
      }
    });
  };
  
  // Custom tooltip to show for data points
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-gray-900 p-3 border border-gray-700 rounded shadow-lg max-w-md">
          <div className="font-bold text-white border-b border-gray-600 pb-1 mb-2">
            {data.label || "State"}
          </div>
          <p className="text-gray-300">{`Time: ${formatXAxis(label)}`}</p>
          
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <p className="font-semibold text-blue-300">Resources:</p>
              <p style={{ color: '#8884d8' }}>{`Metal: ${Math.floor(data.metal || 0)}`}</p>
              <p style={{ color: '#82ca9d' }}>{`Crystal: ${Math.floor(data.crystal || 0)}`}</p>
              <p style={{ color: '#ffc658' }}>{`Deuterium: ${Math.floor(data.deuterium || 0)}`}</p>
            </div>
            <div>
              <p className="font-semibold text-blue-300">Production:</p>
              <p style={{ color: '#8884d8' }}>{`Metal: ${Math.floor(data.metalProd || 0)}/h`}</p>
              <p style={{ color: '#82ca9d' }}>{`Crystal: ${Math.floor(data.crystalProd || 0)}/h`}</p>
              <p style={{ color: '#ffc658' }}>{`Deuterium: ${Math.floor(data.deuteriumProd || 0)}/h`}</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Effect to handle window resize and trigger chart updates
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      // Debounce resize events to avoid excessive re-renders
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setResizeKey(prev => prev + 1);
      }, 150);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Also listen for orientation changes and manual resize triggers
    window.addEventListener('orientationchange', handleResize);
    
    // Clean up
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return (
    <div className="plot-content p-4 bg-gray-800 text-white min-h-screen flex flex-col h-screen">
      <h1 className="text-2xl font-bold mb-4">Resource Evolution</h1>
      
      <div className="mb-4">
        <label className="mr-2">Planet:</label>
        <select 
          className="bg-gray-700 text-white p-2 rounded"
          value={selectedPlanetId}
          onChange={(e) => setSelectedPlanetId(e.target.value)}
        >
          {Object.keys(currentGameState.planets).map((id) => (
            <option key={id} value={id}>{currentGameState.planets[id].name}</option>
          ))}
        </select>
      </div>
      
      {chartData.length > 0 ? (
        <div className="flex-grow flex flex-col">
          <div className="flex-1 mb-4" id="resource-chart-container">
            <h2 className="text-xl font-semibold mb-2 flex justify-between items-center">
              <span>Resource Evolution Over Time</span>
              <button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-sm"
                onClick={handleZoomOut}
              >
                Reset Zoom
              </button>
            </h2>
            <ResponsiveContainer key={`resource-${resizeKey}`} width="100%" height="100%">
              <LineChart
                ref={resourceChartRef}
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                onMouseDown={(e) => handleMouseDown('resourceChart', e)}
                onMouseMove={(e) => handleMouseMove('resourceChart', e)}
                onMouseUp={() => handleMouseUp('resourceChart')}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  tickFormatter={formatXAxis}
                  scale="linear"
                  type="number"
                  domain={calculateXDomain(chartData, zoomState.resourceChart.left, zoomState.resourceChart.right)}
                  ticks={generateEvenlySpacedTicks(chartData, zoomState.resourceChart.left, zoomState.resourceChart.right)}
                  interval={0}
                  allowDataOverflow
                  padding={{ left: 10, right: 10 }}
                  label={{ value: 'Time', position: 'insideBottomRight', offset: -10 }} 
                />
                <YAxis 
                  label={{ value: 'Resources', angle: -90, position: 'insideLeft' }}
                  domain={[zoomState.resourceChart.top, zoomState.resourceChart.bottom]}
                  allowDataOverflow
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="linear" 
                  dataKey="metal" 
                  name="Metal" 
                  stroke="#8884d8" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                <Line 
                  type="linear" 
                  dataKey="crystal" 
                  name="Crystal" 
                  stroke="#82ca9d" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                <Line 
                  type="linear" 
                  dataKey="deuterium" 
                  name="Deuterium" 
                  stroke="#ffc658" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                {zoomState.resourceChart.refAreaLeft && zoomState.resourceChart.refAreaRight ? (
                  <ReferenceLine
                    x={zoomState.resourceChart.refAreaLeft}
                    stroke="red"
                    strokeDasharray="3 3"
                  />
                ) : null}
                {zoomState.resourceChart.refAreaLeft && zoomState.resourceChart.refAreaRight ? (
                  <ReferenceLine
                    x={zoomState.resourceChart.refAreaRight}
                    stroke="red"
                    strokeDasharray="3 3"
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="flex-1" id="production-chart-container">
            <h2 className="text-xl font-semibold mb-2 flex justify-between items-center">
              <span>Production Evolution Over Time</span>
              <button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-sm"
                onClick={handleZoomOut}
              >
                Reset Zoom
              </button>
            </h2>
            <ResponsiveContainer key={`production-${resizeKey}`} width="100%" height="100%">
              <LineChart
                ref={productionChartRef}
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                onMouseDown={(e) => handleMouseDown('productionChart', e)}
                onMouseMove={(e) => handleMouseMove('productionChart', e)}
                onMouseUp={() => handleMouseUp('productionChart')}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time" 
                  tickFormatter={formatXAxis}
                  scale="linear"
                  type="number"
                  domain={calculateXDomain(chartData, zoomState.productionChart.left, zoomState.productionChart.right)}
                  ticks={generateEvenlySpacedTicks(chartData, zoomState.productionChart.left, zoomState.productionChart.right)}
                  interval={0}
                  allowDataOverflow
                  padding={{ left: 10, right: 10 }}
                  label={{ value: 'Time', position: 'insideBottomRight', offset: -10 }} 
                />
                <YAxis 
                  label={{ value: 'Production/hour', angle: -90, position: 'insideLeft' }}
                  domain={[zoomState.productionChart.top, zoomState.productionChart.bottom]}
                  allowDataOverflow
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="stepAfter" 
                  dataKey="metalProd" 
                  name="Metal Prod" 
                  stroke="#8884d8" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                <Line 
                  type="stepAfter" 
                  dataKey="crystalProd" 
                  name="Crystal Prod" 
                  stroke="#82ca9d" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                <Line 
                  type="stepAfter" 
                  dataKey="deuteriumProd" 
                  name="Deuterium Prod" 
                  stroke="#ffc658" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                <Line 
                  type="stepAfter" 
                  dataKey="energyProd" 
                  name="Energy Prod" 
                  stroke="#ff8a65" 
                  dot={true}
                  connectNulls={true}
                  isAnimationActive={false}
                />
                {zoomState.productionChart.refAreaLeft && zoomState.productionChart.refAreaRight ? (
                  <ReferenceLine
                    x={zoomState.productionChart.refAreaLeft}
                    stroke="red"
                    strokeDasharray="3 3"
                  />
                ) : null}
                {zoomState.productionChart.refAreaLeft && zoomState.productionChart.refAreaRight ? (
                  <ReferenceLine
                    x={zoomState.productionChart.refAreaRight}
                    stroke="red"
                    strokeDasharray="3 3"
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-700 rounded text-center">
          <p>No data available for the selected planet.</p>
          <p>Try selecting a different planet or check if any actions have been performed.</p>
        </div>
      )}

    </div>
  );
};

export default PlotContent;
